import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Star, MessageSquare, Users, Briefcase, TrendingUp, Mail, Clock, CheckCircle, XCircle, Copy, Search, UserCheck, UserX, Trash2, Archive, ArchiveRestore, Pencil, BookOpen, Settings, Megaphone, HelpCircle, ArrowUpDown, Scale, Bell, Key, Zap, ExternalLink, Eye, EyeOff, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatPhoneNumber, formatPhoneInput } from '../utils/phoneFormat';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { useAdminSettings } from '../hooks/useAdminSettings';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [showInviteStaffModal, setShowInviteStaffModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserTarget, setEditUserTarget] = useState(null);
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserPassword, setEditUserPassword] = useState('');
  const [showArchivedCases, setShowArchivedCases] = useState(false);
  const [staffEmail, setStaffEmail] = useState('');
  const [staffName, setStaffName] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [caseSortOrder, setCaseSortOrder] = useState('name_asc');
  
  // Fetch dynamic statuses from admin settings
  const { getStatusOptions, getMilestoneOptions, loading: settingsLoading } = useAdminSettings();
  const caseStatusOptions = getStatusOptions();
  const milestoneOptions = getMilestoneOptions();
  
  // Zapier Integration state
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [showKeyValue, setShowKeyValue] = useState(false);
  
  const [newCase, setNewCase] = useState({
    case_name: '',
    case_id_custom: '',
    case_type: 'personal_injury',
    current_status: 'case_initiation',
    client_name: '',
    client_email: '',
    client_phone: '',
    milestones: []
  });

  const [creatingCase, setCreatingCase] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesRes, clientsRes, staffRes, feedbackRes, invitationsRes, apiKeysRes] = await Promise.all([
        fetch(`${API}/cases`, { credentials: 'include' }),
        fetch(`${API}/users/clients`, { credentials: 'include' }),
        fetch(`${API}/users/staff?include_admins=true`, { credentials: 'include' }),
        fetch(`${API}/feedback`, { credentials: 'include' }),
        fetch(`${API}/invitations`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/zapier/keys`, { credentials: 'include' })
      ]);

      if (casesRes.ok) setCases(await casesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (staffRes.ok) setStaff(await staffRes.json());
      if (feedbackRes.ok) setFeedback(await feedbackRes.json());
      if (invitationsRes.ok) setInvitations(await invitationsRes.json());
      if (apiKeysRes.ok) {
        const keysData = await apiKeysRes.json();
        setApiKeys(keysData.api_keys || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Zapier API Key Management
  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/zapier/keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newKeyName })
      });
      
      if (!response.ok) throw new Error('Failed to create API key');
      
      const data = await response.json();
      setCreatedKey(data);
      setNewKeyName('');
      toast.success('API key created successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to create API key');
    }
  };
  
  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/zapier/keys/${keyId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to revoke API key');
      
      toast.success('API key revoked successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleInviteStaff = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          email: staffEmail, 
          role: 'staff',
          invitee_name: staffName || null
        })
      });

      if (!response.ok) throw new Error('Failed to create invitation');
      
      const invitation = await response.json();
      toast.success(`Staff invitation sent! Code: ${invitation.invitation_code}`);
      setShowInviteStaffModal(false);
      setStaffEmail('');
      setStaffName('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    
    if (!newCase.case_name || !newCase.case_id_custom || !newCase.current_status || !newCase.client_name || !newCase.client_email || !newCase.client_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreatingCase(true);
    try {
      const requestBody = {
        case_name: newCase.case_name,
        case_id_custom: newCase.case_id_custom,
        case_type: newCase.case_type,
        current_status: newCase.current_status,
        client_name: newCase.client_name,
        client_email: newCase.client_email,
        client_phone: newCase.client_phone,
        milestones: newCase.milestones.length > 0 ? newCase.milestones : null
      };
      
      const response = await fetch(`${API}/cases/with-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const status = response.status;
      const statusOk = response.ok;
      
      // Read the body as text first, then parse
      let data = {};
      try {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        }
      } catch (parseErr) {
        console.error('Failed to parse response:', parseErr);
      }

      if (!statusOk) {
        // Handle different error formats
        const errorMsg = data.detail || data.message || data.error || `Request failed with status ${status}`;
        throw new Error(errorMsg);
      }
      
      // Handle response based on whether client exists
      if (data.client_exists) {
        if (data.email_delivered === false) {
          toast.error(`Case created for existing client, BUT email notification FAILED: ${data.email_error || 'unknown error'}. Please contact them directly.`, { duration: 10000 });
        } else {
          toast.success(`Case created for existing client! They can view it in their portal.`);
        }
      } else {
        if (data.email_delivered === false) {
          toast.error(`Case created, BUT invitation email FAILED: ${data.email_error || 'unknown error'}. Share this code manually: ${data.invitation_code}`, { duration: 15000 });
        } else {
          toast.success(`Case created! Client invitation code: ${data.invitation_code}`);
        }
      }
      setShowCaseModal(false);
      setNewCase({ case_name: '', case_id_custom: '', case_type: 'personal_injury', current_status: 'case_initiation', client_name: '', client_email: '', client_phone: '', milestones: [] });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to create case');
    } finally {
      setCreatingCase(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      let endpoint;
      if (deleteTarget.type === 'client' || deleteTarget.type === 'staff') {
        endpoint = `${API}/users/${deleteTarget.id}`;
      } else if (deleteTarget.type === 'case') {
        endpoint = `${API}/cases/${deleteTarget.id}`;
      }
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const status = response.status;
      const statusOk = response.ok;
      const data = await response.json().catch(() => ({}));
      
      if (!statusOk) {
        throw new Error(data.detail || `Request failed with status ${status}`);
      }
      
      toast.success(`${deleteTarget.name} deleted successfully`);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  const handleArchiveCase = async (caseId, caseName, isArchived) => {
    try {
      const response = await fetch(`${API}/cases/${caseId}/archive`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const status = response.status;
      const statusOk = response.ok;
      const data = await response.json().catch(() => ({}));

      if (!statusOk) {
        throw new Error(data.detail || `Request failed with status ${status}`);
      }
      
      toast.success(data.message || 'Case archived successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to archive case');
    }
  };

  const confirmDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setShowDeleteModal(true);
  };

  const handleCancelInvitation = async (invitationCode, email) => {
    try {
      const response = await fetch(`${API}/invitations/${invitationCode}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel invitation');
      }
      
      toast.success(`Invitation to ${email} cancelled`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleArchiveInvitation = async (invitationCode) => {
    try {
      const response = await fetch(`${API}/invitations/${invitationCode}/archive`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to archive invitation');
      }
      
      const result = await response.json();
      toast.success(result.archived ? 'Invitation archived' : 'Invitation unarchived');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteInvitation = async (invitationCode, email) => {
    if (!window.confirm(`Are you sure you want to permanently delete the invitation for ${email}?`)) return;
    
    try {
      const response = await fetch(`${API}/invitations/${invitationCode}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete invitation');
      }
      
      toast.success(`Invitation to ${email} deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResendInvitation = async (invitationCode, email) => {
    try {
      const response = await fetch(`${API}/invitations/${invitationCode}/resend`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend invitation');
      }
      
      toast.success(`Invitation resent to ${email}`);
      if (data.new_code) {
        fetchData(); // Refresh to show new code if it was renewed
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditUserModal = (user, type) => {
    setEditUserTarget({ ...user, type });
    setEditUserEmail(user.email);
    setEditUserPassword('');
    setShowEditUserModal(true);
  };

  const handleUpdateEmail = async () => {
    if (!editUserTarget || !editUserEmail.trim()) return;
    
    try {
      const response = await fetch(`${API}/users/${editUserTarget.user_id}/email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: editUserEmail })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update email');
      }
      
      toast.success('Email updated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateRole = async (newRole) => {
    if (!editUserTarget || !newRole) return;
    if (editUserTarget.role === newRole) return;

    try {
      const response = await fetch(`${API}/users/${editUserTarget.user_id}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to update role');
      }

      toast.success(`Role updated to ${newRole}`);
      setEditUserTarget({ ...editUserTarget, role: newRole });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetPassword = async () => {
    if (!editUserTarget || !editUserPassword.trim()) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (editUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const response = await fetch(`${API}/users/${editUserTarget.user_id}/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: editUserPassword })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reset password');
      }
      
      toast.success('Password reset successfully');
      setEditUserPassword('');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  const getAverageRating = () => {
    if (feedback.length === 0) return 0;
    const total = feedback.reduce((sum, f) => sum + f.rating, 0);
    return (total / feedback.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    feedback.forEach(f => {
      dist[f.rating] = (dist[f.rating] || 0) + 1;
    });
    return dist;
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'service': return 'Overall Service';
      case 'communication': return 'Communication';
      case 'outcome': return 'Case Outcome';
      default: return category;
    }
  };

  const getRatingLabel = (rating) => {
    switch (rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return '';
    }
  };

  const getInvitationStatus = (invitation) => {
    if (invitation.used) {
      return { label: 'Accepted', color: 'bg-green-100 text-green-800', icon: CheckCircle, key: 'accepted' };
    }
    const now = new Date();
    const expiresAt = new Date(invitation.expires_at);
    if (expiresAt < now) {
      return { label: 'Expired', color: 'bg-red-100 text-red-800', icon: XCircle, key: 'expired' };
    }
    return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock, key: 'pending' };
  };

  const copyInvitationCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invitation code copied to clipboard');
  };

  // State for collapsible invitation groups
  const [expandedInvitationGroups, setExpandedInvitationGroups] = useState({
    pending: true,
    accepted: true,
    expired: true
  });
  
  // State for invitation search and filters
  const [invitationSearchQuery, setInvitationSearchQuery] = useState('');
  const [showArchivedInvitations, setShowArchivedInvitations] = useState(false);

  const toggleInvitationGroup = (group) => {
    setExpandedInvitationGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };
  
  // Archive all invitations
  const handleArchiveAllInvitations = async (status) => {
    const statusLabel = status === 'all' ? 'all' : status;
    if (!window.confirm(`Are you sure you want to archive ${statusLabel} invitations?`)) return;
    
    try {
      const response = await fetch(`${API}/invitations/archive-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: status })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to archive invitations');
      }
      
      const result = await response.json();
      toast.success(`Archived ${result.archived_count} invitation(s)`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  // Delete all invitations
  const handleDeleteAllInvitations = async (status) => {
    const statusLabel = status === 'all' ? 'all' : status;
    if (!window.confirm(`Are you sure you want to permanently delete ${statusLabel} invitations? This cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${API}/invitations/delete-bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: status })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete invitations');
      }
      
      const result = await response.json();
      toast.success(`Deleted ${result.deleted_count} invitation(s)`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  // Filter invitations based on search query
  const filterInvitations = (invitationList) => {
    if (!invitationSearchQuery.trim()) return invitationList;
    const query = invitationSearchQuery.toLowerCase();
    return invitationList.filter(inv => 
      inv.email?.toLowerCase().includes(query) ||
      inv.invitee_name?.toLowerCase().includes(query) ||
      inv.invitation_code?.toLowerCase().includes(query) ||
      inv.role?.toLowerCase().includes(query)
    );
  };
  
  // Filter by archived status first
  const activeInvitations = invitations.filter(inv => !inv.archived);
  const archivedInvitations = invitations.filter(inv => inv.archived);
  
  // Get invitations based on archived toggle
  const displayInvitations = showArchivedInvitations ? archivedInvitations : activeInvitations;

  const getPendingInvitations = () => filterInvitations(displayInvitations.filter(inv => {
    if (inv.used) return false;
    const expiresAt = new Date(inv.expires_at);
    return expiresAt >= new Date();
  }));

  const getAcceptedInvitations = () => filterInvitations(displayInvitations.filter(inv => inv.used));

  const getExpiredInvitations = () => filterInvitations(displayInvitations.filter(inv => {
    if (inv.used) return false;
    const expiresAt = new Date(inv.expires_at);
    return expiresAt < new Date();
  }));

  // Filter cases based on search query
  const filteredCases = cases.filter(caseItem => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      caseItem.case_name?.toLowerCase().includes(query) ||
      caseItem.case_number?.toLowerCase().includes(query) ||
      caseItem.client_name?.toLowerCase().includes(query) ||
      caseItem.case_type?.toLowerCase().includes(query) ||
      caseItem.current_status?.toLowerCase().replace(/_/g, ' ').includes(query)
    );
  });

  // Invitation Card component for reuse in groups
  const InvitationCard = ({ invitation }) => {
    const status = getInvitationStatus(invitation);
    const StatusIcon = status.icon;
    return (
      <div
        className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-[#80298F]/30 transition-colors overflow-hidden"
        data-testid={`invitation-${invitation.invitation_code}`}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="min-w-0">
                  {invitation.invitee_name && (
                    <span className="font-semibold text-gray-900 text-sm sm:text-base mr-1">{invitation.invitee_name}</span>
                  )}
                  <span className={`text-xs sm:text-sm truncate ${invitation.invitee_name ? 'text-gray-600' : 'font-medium text-gray-900'}`}>
                    {invitation.invitee_name ? `(${invitation.email})` : invitation.email}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  invitation.role === 'client' 
                    ? 'bg-[#80298F]/10 text-[#80298F]' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {invitation.role}
                </span>
              </div>
            </div>
            {!invitation.used && (
              <div className="flex gap-1 flex-shrink-0 self-end sm:self-start">
                <button
                  onClick={() => handleResendInvitation(invitation.invitation_code, invitation.email)}
                  className="p-2 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded-lg transition-colors"
                  title="Resend invitation"
                  data-testid={`resend-invitation-${invitation.invitation_code}`}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            )}
            {/* Archive and Delete buttons - always visible */}
            <div className="flex gap-1 flex-shrink-0 self-end sm:self-start">
              <button
                onClick={() => handleArchiveInvitation(invitation.invitation_code)}
                className="p-2 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded-lg transition-colors"
                title={invitation.archived ? "Unarchive invitation" : "Archive invitation"}
                data-testid={`archive-invitation-${invitation.invitation_code}`}
              >
                {invitation.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              </button>
              <button
                onClick={() => handleDeleteInvitation(invitation.invitation_code, invitation.email)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete invitation"
                data-testid={`delete-invitation-${invitation.invitation_code}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              Code: <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono">{invitation.invitation_code}</code>
            </span>
            <button
              onClick={() => copyInvitationCode(invitation.invitation_code)}
              className="text-[#80298F] hover:text-[#6B2278] flex items-center gap-1"
              title="Copy code"
            >
              <Copy className="h-3 w-3" />
              Copy
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
            <span className="truncate">By: {invitation.invited_by_name || invitation.invited_by}</span>
            <span className="hidden sm:inline">•</span>
            <span>{formatDate(invitation.created_at)}</span>
            <span>→</span>
            <span>{formatDate(invitation.expires_at)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Sort cases based on selected sort order
  const sortCases = (casesToSort) => {
    return [...casesToSort].sort((a, b) => {
      switch (caseSortOrder) {
        case 'name_asc':
          return (a.case_name || '').localeCompare(b.case_name || '');
        case 'name_desc':
          return (b.case_name || '').localeCompare(a.case_name || '');
        case 'date_asc':
          return new Date(a.updated_at || a.created_at || 0) - new Date(b.updated_at || b.created_at || 0);
        case 'date_desc':
          return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
        default:
          return 0;
      }
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  const ratingDist = getRatingDistribution();

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      {/* Action Buttons Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Mobile: Scrollable horizontal layout */}
          <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <Button 
              onClick={() => setShowCaseModal(true)}
              className="bg-[#80298F] hover:bg-[#6B2278] text-xs sm:text-sm flex-shrink-0"
              data-testid="create-case-button"
            >
              <Plus className="h-4 w-4 mr-1 sm:mr-2" />
              <span>New Case</span>
            </Button>
            <Button 
              onClick={() => setShowInviteStaffModal(true)} 
              className="bg-[#FAA519] hover:bg-[#E59415] text-black text-xs sm:text-sm flex-shrink-0"
              data-testid="invite-staff-button"
            >
              <Users className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Invite Staff</span>
              <span className="sm:hidden">Staff</span>
            </Button>
            <Button 
              onClick={() => navigate('/admin/resources')}
              variant="outline"
              className="text-xs sm:text-sm flex-shrink-0"
              data-testid="manage-resources-button"
            >
              <BookOpen className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Manage Resources</span>
              <span className="sm:hidden">Resources</span>
            </Button>
            <Button 
              onClick={() => navigate('/admin/firm-settings')}
              variant="outline"
              className="text-xs sm:text-sm flex-shrink-0"
              data-testid="firm-settings-button"
            >
              <Settings className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Firm Settings</span>
              <span className="sm:hidden">Settings</span>
            </Button>
            <Button 
              onClick={() => navigate('/admin/announcements')}
              variant="outline"
              className="text-xs sm:text-sm flex-shrink-0"
              data-testid="announcements-button"
            >
              <Megaphone className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Announcements</span>
              <span className="sm:hidden">News</span>
            </Button>
            <Button 
              onClick={() => navigate('/admin/faqs')}
              variant="outline"
              className="text-xs sm:text-sm flex-shrink-0"
              data-testid="manage-faqs-button"
            >
              <HelpCircle className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Manage FAQs</span>
              <span className="sm:hidden">FAQs</span>
            </Button>
            <Button 
              onClick={() => navigate('/admin/glossary')}
              variant="outline"
              className="text-xs sm:text-sm flex-shrink-0"
              data-testid="manage-glossary-button"
            >
              <Scale className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Manage Glossary</span>
              <span className="sm:hidden">Glossary</span>
            </Button>
            <Button 
              onClick={() => navigate('/admin/reminder-settings')}
              variant="outline"
              className="text-xs sm:text-sm flex-shrink-0"
              data-testid="reminder-settings-button"
            >
              <Bell className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Reminder Settings</span>
              <span className="sm:hidden">Reminders</span>
            </Button>
            <Button
              onClick={() => setActiveTab('integrations')}
              variant={activeTab === 'integrations' ? 'default' : 'outline'}
              className={`text-xs sm:text-sm flex-shrink-0 ${activeTab === 'integrations' ? 'bg-[#80298F] hover:bg-[#6b2278]' : ''}`}
              data-testid="integrations-button-top"
            >
              <Zap className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Integrations</span>
              <span className="sm:hidden">Zapier</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/settings')}
              variant="outline"
              className="text-xs sm:text-sm flex-shrink-0"
              data-testid="admin-settings-button"
            >
              <Settings className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
              <span className="sm:hidden">Settings</span>
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900" data-testid="admin-heading">Admin Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage cases, clients, and review feedback</p>
        </div>

        {/* Stats Cards - 2 columns on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Total Cases</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#80298F] mt-1">{cases.length}</p>
                </div>
                <Briefcase className="h-8 w-8 sm:h-10 sm:w-10 text-[#80298F]/20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Total Clients</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#FAA519] mt-1">{clients.length}</p>
                </div>
                <Users className="h-8 w-8 sm:h-10 sm:w-10 text-[#FAA519]/20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Active Cases</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#DC3239] mt-1">
                    {cases.filter(c => c.current_status !== 'case_closed').length}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-[#DC3239]/20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Avg. Rating</p>
                  <div className="flex items-center mt-1">
                    <p className="text-2xl sm:text-3xl font-bold text-[#FAA519]">{getAverageRating()}</p>
                    <Star className="h-5 w-5 sm:h-6 sm:w-6 text-[#FAA519] fill-[#FAA519] ml-1" />
                  </div>
                </div>
                <MessageSquare className="h-8 w-8 sm:h-10 sm:w-10 text-[#FAA519]/20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs - Scrollable on mobile */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto pb-px scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'overview'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="overview-tab"
            >
              Cases
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`pb-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'invitations'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="invitations-tab"
            >
              Invitations ({invitations.length})
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`pb-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'feedback'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="feedback-tab"
            >
              Feedback ({feedback.length})
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`pb-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'clients'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="clients-tab"
            >
              Clients ({clients.length})
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`pb-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'staff'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="staff-tab"
            >
              Staff ({staff.length})
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={`pb-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${
                activeTab === 'integrations'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="integrations-tab"
            >
              <Zap className="h-4 w-4" />
              Integrations
            </button>
          </nav>
        </div>

        {/* Cases Overview Tab */}
        {activeTab === 'overview' && (
          <Card data-testid="cases-list">
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-lg sm:text-xl">All Cases</CardTitle>
                    <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showArchivedCases}
                        onChange={(e) => setShowArchivedCases(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Archived
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={caseSortOrder} onValueChange={setCaseSortOrder}>
                    <SelectTrigger className="w-full sm:w-48 text-sm" data-testid="case-sort-select">
                      <ArrowUpDown className="h-4 w-4 mr-2 text-gray-400" />
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name_asc">Name (A to Z)</SelectItem>
                      <SelectItem value="name_desc">Name (Z to A)</SelectItem>
                      <SelectItem value="date_desc">Updated (Latest first)</SelectItem>
                      <SelectItem value="date_asc">Updated (Earliest first)</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1 sm:max-w-xs">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search cases..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 text-sm"
                      data-testid="case-search-input"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              {(() => {
                const displayCases = sortCases(showArchivedCases 
                  ? filteredCases 
                  : filteredCases.filter(c => !c.is_archived));
                const archivedCount = cases.filter(c => c.is_archived).length;
                
                return cases.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No cases yet</p>
                ) : displayCases.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {searchQuery ? `No cases match "${searchQuery}"` : 'No active cases'}
                    </p>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="text-[#80298F] hover:underline mt-2 text-sm"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs sm:text-sm text-gray-500">
                      Showing {displayCases.length} of {cases.length} cases
                      {archivedCount > 0 && !showArchivedCases && ` (${archivedCount} archived)`}
                    </p>
                    {displayCases.map((caseItem) => (
                      <div
                        key={caseItem.case_id}
                        className={`border rounded-lg p-3 sm:p-4 hover:shadow-md transition-all ${
                          caseItem.is_archived 
                            ? 'border-gray-300 bg-gray-50' 
                            : 'border-gray-200 hover:border-[#80298F]'
                        }`}
                        data-testid={`case-${caseItem.case_id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4">
                          <Link to={`/cases/${caseItem.case_id}`} className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className={`font-semibold text-sm sm:text-base ${caseItem.is_archived ? 'text-gray-500' : 'text-gray-900'}`}>
                                {caseItem.case_name}
                              </h3>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-mono">
                                {caseItem.case_number}
                              </span>
                              {caseItem.is_archived && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                                  Archived
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs sm:text-sm text-gray-600">Client: {caseItem.client_name}</p>
                              {caseItem.client_registered === true && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700" title="Client has registered">
                                  <UserCheck className="h-3 w-3" />
                                  <span className="hidden sm:inline">Registered</span>
                                </span>
                              )}
                              {caseItem.client_registered === false && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title="Client has not registered yet">
                                  <UserX className="h-3 w-3" />
                                  <span className="hidden sm:inline">Pending</span>
                                </span>
                              )}
                              {caseItem.client_registered === null && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" title="No portal account linked to this client">
                                  <UserX className="h-3 w-3" />
                                  <span className="hidden sm:inline">No Account</span>
                                </span>
                              )}
                            </div>
                            <div className="mt-1 sm:mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                              <span>{caseItem.case_type.replace('_', ' ')}</span>
                              <span className="hidden sm:inline">•</span>
                              <span>{formatDate(caseItem.status_updated_at)}</span>
                            </div>
                          </Link>
                          <div className="flex items-center justify-between sm:justify-end gap-2 mt-2 sm:mt-0">
                            <span className="status-badge active text-xs whitespace-nowrap">
                              {caseItem.current_status.replace(/_/g, ' ')}
                            </span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleArchiveCase(caseItem.case_id, caseItem.case_name, caseItem.is_archived);
                              }}
                              className="p-2 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded-lg transition-colors"
                              title={caseItem.is_archived ? 'Unarchive case' : 'Archive case'}
                            >
                              {caseItem.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                confirmDelete('case', caseItem.case_id, caseItem.case_name);
                              }}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete case"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Invitations Tab */}
        {activeTab === 'invitations' && (
          <div className="space-y-4 sm:space-y-6" data-testid="invitations-section">
            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search invitations by name, email, or code..."
                  value={invitationSearchQuery}
                  onChange={(e) => setInvitationSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="invitation-search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  variant={showArchivedInvitations ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowArchivedInvitations(!showArchivedInvitations)}
                  className={showArchivedInvitations ? "bg-[#80298F] hover:bg-[#6B2278]" : ""}
                  data-testid="toggle-archived-invitations"
                >
                  {showArchivedInvitations ? <ArchiveRestore className="h-4 w-4 mr-1" /> : <Archive className="h-4 w-4 mr-1" />}
                  {showArchivedInvitations ? "Show Active" : "Show Archived"}
                </Button>
                {!showArchivedInvitations && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleArchiveAllInvitations('all')}
                      data-testid="archive-all-invitations"
                    >
                      <Archive className="h-4 w-4 mr-1" />
                      Archive All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteAllInvitations('all')}
                      className="text-[#DC3239] border-[#DC3239] hover:bg-[#DC3239] hover:text-white"
                      data-testid="delete-all-invitations"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete All
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Archived indicator */}
            {showArchivedInvitations && (
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 flex items-center gap-2">
                <Archive className="h-5 w-5 text-gray-500" />
                <span className="text-sm text-gray-600">Showing archived invitations ({archivedInvitations.length} total)</span>
              </div>
            )}
            
            {/* Invitations Summary */}
            <div className="grid grid-cols-3 gap-2 sm:gap-6">
              <Card>
                <CardContent className="p-3 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Pending</p>
                      <p className="text-xl sm:text-3xl font-bold text-[#FAA519] mt-1">
                        {getPendingInvitations().length}
                      </p>
                    </div>
                    <Clock className="h-6 w-6 sm:h-10 sm:w-10 text-[#FAA519]/20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Accepted</p>
                      <p className="text-xl sm:text-3xl font-bold text-green-600 mt-1">
                        {getAcceptedInvitations().length}
                      </p>
                    </div>
                    <CheckCircle className="h-6 w-6 sm:h-10 sm:w-10 text-green-600/20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Expired</p>
                      <p className="text-xl sm:text-3xl font-bold text-[#DC3239] mt-1">
                        {getExpiredInvitations().length}
                      </p>
                    </div>
                    <XCircle className="h-6 w-6 sm:h-10 sm:w-10 text-[#DC3239]/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Invitations List - Grouped by Status */}
            {invitations.length === 0 ? (
              <Card>
                <CardContent className="px-4 sm:px-6 py-8">
                  <div className="text-center">
                    <Mail className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No invitations sent yet</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Pending Invitations */}
                {getPendingInvitations().length > 0 && (
                  <Card>
                    <button
                      onClick={() => toggleInvitationGroup('pending')}
                      className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      data-testid="toggle-pending-invitations"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                          <Clock className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Pending</h3>
                          <p className="text-sm text-gray-500">{getPendingInvitations().length} invitation{getPendingInvitations().length !== 1 ? 's' : ''} awaiting response</p>
                        </div>
                      </div>
                      {expandedInvitationGroups.pending ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedInvitationGroups.pending && (
                      <CardContent className="px-4 sm:px-6 pt-0 pb-4">
                        <div className="space-y-3">
                          {getPendingInvitations().map((invitation) => (
                            <InvitationCard key={invitation.invitation_code} invitation={invitation} />
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* Accepted Invitations */}
                {getAcceptedInvitations().length > 0 && (
                  <Card>
                    <button
                      onClick={() => toggleInvitationGroup('accepted')}
                      className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      data-testid="toggle-accepted-invitations"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Accepted</h3>
                          <p className="text-sm text-gray-500">{getAcceptedInvitations().length} invitation{getAcceptedInvitations().length !== 1 ? 's' : ''} accepted</p>
                        </div>
                      </div>
                      {expandedInvitationGroups.accepted ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedInvitationGroups.accepted && (
                      <CardContent className="px-4 sm:px-6 pt-0 pb-4">
                        <div className="space-y-3">
                          {getAcceptedInvitations().map((invitation) => (
                            <InvitationCard key={invitation.invitation_code} invitation={invitation} />
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* Expired Invitations */}
                {getExpiredInvitations().length > 0 && (
                  <Card>
                    <button
                      onClick={() => toggleInvitationGroup('expired')}
                      className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      data-testid="toggle-expired-invitations"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <XCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Expired</h3>
                          <p className="text-sm text-gray-500">{getExpiredInvitations().length} invitation{getExpiredInvitations().length !== 1 ? 's' : ''} expired</p>
                        </div>
                      </div>
                      {expandedInvitationGroups.expired ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedInvitationGroups.expired && (
                      <CardContent className="px-4 sm:px-6 pt-0 pb-4">
                        <div className="space-y-3">
                          {getExpiredInvitations().map((invitation) => (
                            <InvitationCard key={invitation.invitation_code} invitation={invitation} />
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* Client Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-4 sm:space-y-6" data-testid="feedback-section">
            {/* Rating Summary */}
            <Card>
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="text-lg sm:text-xl">Rating Summary</CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Average Rating */}
                  <div className="flex items-center space-x-4 sm:space-x-6">
                    <div className="text-center">
                      <p className="text-4xl sm:text-5xl font-bold text-[#FAA519]">{getAverageRating()}</p>
                      <div className="flex justify-center mt-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-4 w-4 sm:h-5 sm:w-5 ${
                              star <= Math.round(getAverageRating())
                                ? 'fill-[#FAA519] text-[#FAA519]'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">{feedback.length} reviews</p>
                    </div>
                  </div>

                  {/* Rating Distribution */}
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <div key={rating} className="flex items-center space-x-2 sm:space-x-3">
                        <span className="text-xs sm:text-sm text-gray-600 w-12 sm:w-16">{rating} stars</span>
                        <div className="flex-1 h-2 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#FAA519] rounded-full"
                            style={{
                              width: feedback.length > 0
                                ? `${(ratingDist[rating] / feedback.length) * 100}%`
                                : '0%'
                            }}
                          />
                        </div>
                        <span className="text-xs sm:text-sm text-gray-500 w-6 sm:w-8">{ratingDist[rating]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feedback List */}
            <Card>
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="text-lg sm:text-xl">All Feedback</CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {feedback.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No feedback received yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {feedback.map((item) => (
                      <div
                        key={item.feedback_id}
                        className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-[#80298F]/30 transition-colors overflow-hidden"
                        data-testid={`feedback-${item.feedback_id}`}
                      >
                        <div className="flex flex-col gap-3">
                          {/* Stars and badges row */}
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-4 w-4 ${
                                    star <= item.rating
                                      ? 'fill-[#FAA519] text-[#FAA519]'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-xs sm:text-sm font-medium text-gray-900">
                              {getRatingLabel(item.rating)}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#80298F]/10 text-[#80298F]">
                              {getCategoryLabel(item.category)}
                            </span>
                            {item.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                item.status === 'resolved' ? 'bg-green-100 text-green-800' :
                                item.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                                item.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || 'Pending'}
                              </span>
                            )}
                          </div>
                          
                          {/* Comment text */}
                          <p className="text-sm sm:text-base text-gray-700">{item.comments}</p>
                          
                          {/* Meta info row */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                            <span>{item.user_name || 'Anonymous'}</span>
                            <span>•</span>
                            <span>{formatDate(item.created_at)}</span>
                            {item.case_id && (
                              <>
                                <span>•</span>
                                <Link
                                  to={`/cases/${item.case_id}`}
                                  className="text-[#80298F] hover:underline"
                                >
                                  View Case
                                </Link>
                              </>
                            )}
                          </div>
                          
                          {/* Actions row */}
                          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                            <select
                              value={item.status || 'pending'}
                              onChange={async (e) => {
                                try {
                                  const response = await fetch(`${API}/feedback/${item.feedback_id}?new_status=${e.target.value}`, {
                                    method: 'PATCH',
                                    credentials: 'include'
                                  });
                                  if (!response.ok) throw new Error('Failed to update status');
                                  toast.success('Feedback status updated');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Failed to update feedback status');
                                }
                              }}
                              className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white hover:border-[#80298F] focus:outline-none focus:ring-1 focus:ring-[#80298F] flex-shrink-0"
                            >
                              <option value="pending">Pending</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="resolved">Resolved</option>
                              <option value="archived">Archived</option>
                            </select>
                            <button
                              onClick={async () => {
                                if (!window.confirm('Are you sure you want to delete this feedback?')) return;
                                try {
                                  const response = await fetch(`${API}/feedback/${item.feedback_id}`, {
                                    method: 'DELETE',
                                    credentials: 'include'
                                  });
                                  if (!response.ok) throw new Error('Failed to delete feedback');
                                  toast.success('Feedback deleted');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Failed to delete feedback');
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete feedback"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Registered Clients Tab */}
        {activeTab === 'clients' && (
          <Card data-testid="clients-section">
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col gap-3">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <UserCheck className="h-5 w-5 text-[#80298F]" />
                  Registered Clients
                </CardTitle>
                <div className="relative w-full sm:max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearchQuery}
                    onChange={(e) => setClientSearchQuery(e.target.value)}
                    className="pl-10 text-sm"
                    data-testid="client-search-input"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              {clients.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No clients have registered yet</p>
                  <p className="text-sm text-gray-400 mt-1">Send invitations to clients to get started</p>
                </div>
              ) : (() => {
                const filteredClients = clients.filter(client => {
                  if (!clientSearchQuery.trim()) return true;
                  const query = clientSearchQuery.toLowerCase();
                  const clientCases = cases.filter(c => c.client_id === client.user_id);
                  return (
                    client.name?.toLowerCase().includes(query) ||
                    client.email?.toLowerCase().includes(query) ||
                    clientCases.some(c => c.case_name?.toLowerCase().includes(query))
                  );
                });
                
                return filteredClients.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No clients match &quot;{clientSearchQuery}&quot;</p>
                    <button
                      onClick={() => setClientSearchQuery('')}
                      className="text-[#80298F] hover:underline mt-2 text-sm"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs sm:text-sm text-gray-500 mb-3">
                      {clientSearchQuery ? `Showing ${filteredClients.length} of ${clients.length} clients` : `${clients.length} client${clients.length !== 1 ? 's' : ''} registered`}
                    </p>
                    {filteredClients.map((client) => {
                      const clientCases = cases.filter(c => c.client_id === client.user_id);
                      return (
                        <div
                          key={client.user_id}
                          className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-[#80298F]/30 transition-colors overflow-hidden"
                          data-testid={`client-${client.user_id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-[#80298F]/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-base sm:text-lg font-semibold text-[#80298F]">
                                {client.name?.charAt(0)?.toUpperCase() || 'C'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{client.name}</h3>
                              <span className="text-xs sm:text-sm text-gray-600 flex items-center gap-1 truncate">
                                <Mail className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{client.email}</span>
                              </span>
                              {clientCases.length > 0 && (
                                <div className="mt-2 sm:mt-3">
                                  <p className="text-xs text-gray-500 mb-1.5">Cases ({clientCases.length}):</p>
                                  <div className="space-y-1.5">
                                    {clientCases.map(caseItem => (
                                      <Link
                                        key={caseItem.case_id}
                                        to={`/cases/${caseItem.case_id}`}
                                        className="block text-xs sm:text-sm bg-gray-50 rounded px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-[#80298F]/5 transition-colors overflow-hidden"
                                      >
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                          <span className="text-gray-800 font-medium truncate">{caseItem.case_name}</span>
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#FAA519]/20 text-[#80298F] truncate max-w-[150px] self-start sm:self-auto">
                                            {caseItem.current_status?.replace(/_/g, ' ')}
                                          </span>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 justify-end">
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </span>
                            <button
                              onClick={() => openEditUserModal(client, 'client')}
                              className="p-2 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded-lg transition-colors"
                              title="Edit client"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => confirmDelete('client', client.user_id, client.name)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete client"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Staff Tab */}
        {activeTab === 'staff' && (
          <Card data-testid="staff-section">
            <CardHeader className="px-4 sm:px-6">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Users className="h-5 w-5 text-[#80298F]" />
                Staff Members
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              {staff.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No staff members yet</p>
                  <p className="text-sm text-gray-400 mt-1">Send invitations to staff to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs sm:text-sm text-gray-500 mb-3">
                    {staff.length} member{staff.length !== 1 ? 's' : ''}
                  </p>
                  {staff.map((member) => (
                    <div
                      key={member.user_id}
                      className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-[#80298F]/30 transition-colors overflow-hidden"
                      data-testid={`staff-${member.user_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center flex-shrink-0 ${member.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                          <span className={`text-base sm:text-lg font-semibold ${member.role === 'admin' ? 'text-purple-600' : 'text-blue-600'}`}>
                            {member.name?.charAt(0)?.toUpperCase() || 'S'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{member.name}</h3>
                          <span className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{member.email}</span>
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 justify-end">
                        {member.role === 'admin' ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                            Admin
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                            Staff
                          </span>
                        )}
                        <button
                          onClick={() => openEditUserModal(member, 'staff')}
                          className="p-2 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded-lg transition-colors"
                          title={member.role === 'admin' ? 'Edit admin' : 'Edit staff member'}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {member.role !== 'admin' && (
                          <button
                            onClick={() => confirmDelete('staff', member.user_id, member.name)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete staff member"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <div className="space-y-6" data-testid="integrations-tab-content">
            {/* Zapier Integration Card */}
            <Card>
              <CardHeader className="px-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Zap className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Zapier Integration</CardTitle>
                      <p className="text-sm text-gray-500">Connect your portal to 5,000+ apps</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => window.open('/zapier-guide.html', '_blank')}
                      data-testid="zapier-guide-btn"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Setup Guide
                    </Button>
                    <Button
                      onClick={() => setShowCreateKeyModal(true)}
                      className="bg-[#80298F] hover:bg-[#6b2278]"
                      data-testid="create-api-key-btn"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Create API Key
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {/* API Keys List */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    API Keys ({apiKeys.length})
                  </h4>
                  
                  {apiKeys.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Key className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                      <p>No API keys created yet</p>
                      <p className="text-sm mt-1">Create an API key to start connecting Zapier</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {apiKeys.map(key => (
                        <div
                          key={key.key_id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{key.name}</span>
                              {key.is_active ? (
                                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Active</span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">Revoked</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                              <span className="font-mono">{key.key_prefix}...</span>
                              <span>Created: {formatDate(key.created_at)}</span>
                              {key.last_used && (
                                <span>Last used: {formatDate(key.last_used)}</span>
                              )}
                            </div>
                          </div>
                          {key.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevokeApiKey(key.key_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Zapier Actions Documentation */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Available Zapier Actions
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <h5 className="font-medium text-blue-900">Create Case</h5>
                      <p className="text-sm text-blue-700 mt-1">Create a new case for an existing client</p>
                      <code className="text-xs bg-blue-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/create-case</code>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                      <h5 className="font-medium text-green-900">Create Client</h5>
                      <p className="text-sm text-green-700 mt-1">Add a new client to the portal</p>
                      <code className="text-xs bg-green-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/create-client</code>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                      <h5 className="font-medium text-purple-900">Send Message</h5>
                      <p className="text-sm text-purple-700 mt-1">Send a message to a case</p>
                      <code className="text-xs bg-purple-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/send-message</code>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                      <h5 className="font-medium text-orange-900">Create Appointment</h5>
                      <p className="text-sm text-orange-700 mt-1">Schedule an appointment for a case</p>
                      <code className="text-xs bg-orange-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/create-appointment</code>
                    </div>
                    <div className="p-4 bg-pink-50 rounded-lg border border-pink-100">
                      <h5 className="font-medium text-pink-900">Create Task</h5>
                      <p className="text-sm text-pink-700 mt-1">Create a task for a case</p>
                      <code className="text-xs bg-pink-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/create-task</code>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                      <h5 className="font-medium text-yellow-900">Update Case Status</h5>
                      <p className="text-sm text-yellow-700 mt-1">Change a case&apos;s status</p>
                      <code className="text-xs bg-yellow-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/update-case-status</code>
                    </div>
                    <div className="p-4 bg-teal-50 rounded-lg border border-teal-100">
                      <h5 className="font-medium text-teal-900">Update Milestone</h5>
                      <p className="text-sm text-teal-700 mt-1">Add or remove a milestone from a case</p>
                      <code className="text-xs bg-teal-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/update-milestone</code>
                    </div>
                  </div>
                  
                  <h5 className="text-md font-semibold text-gray-700 mt-6 mb-3">GET Endpoints (Retrieve Data)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                      <h5 className="font-medium text-indigo-900">Get All Cases</h5>
                      <p className="text-sm text-indigo-700 mt-1">Retrieve a list of all cases</p>
                      <code className="text-xs bg-indigo-100 px-2 py-1 rounded mt-2 inline-block">GET /api/zapier/cases</code>
                    </div>
                    <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-100">
                      <h5 className="font-medium text-cyan-900">Get Case by ID</h5>
                      <p className="text-sm text-cyan-700 mt-1">Retrieve a specific case by its ID</p>
                      <code className="text-xs bg-cyan-100 px-2 py-1 rounded mt-2 inline-block">GET /api/zapier/cases/&#123;case_id&#125;</code>
                    </div>
                  </div>

                  <h5 className="text-md font-semibold text-gray-700 mt-6 mb-3">Case Management (Update & Delete)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                      <h5 className="font-medium text-emerald-900">Update Case</h5>
                      <p className="text-sm text-emerald-700 mt-1">Update case details (name, type, description, client info)</p>
                      <code className="text-xs bg-emerald-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/update-case</code>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                      <h5 className="font-medium text-red-900">Delete Case</h5>
                      <p className="text-sm text-red-700 mt-1">Archive or permanently delete a case</p>
                      <code className="text-xs bg-red-100 px-2 py-1 rounded mt-2 inline-block">POST /api/zapier/actions/delete-case</code>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-2">How to Use with Zapier</h5>
                    <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                      <li>Create an API key above and copy it securely</li>
                      <li>In Zapier, create a new Zap and add a &quot;Webhooks by Zapier&quot; action</li>
                      <li>Choose &quot;Custom Request&quot; and set the method to GET or POST</li>
                      <li>Enter the endpoint URL (e.g., <code className="bg-gray-200 px-1 rounded">{API_BASE}/api/zapier/actions/create-case</code>)</li>
                      <li>Add header: <code className="bg-gray-200 px-1 rounded">X-API-Key: your_api_key</code></li>
                      <li>Set Content-Type to <code className="bg-gray-200 px-1 rounded">application/json</code></li>
                      <li>Add the JSON body with required fields</li>
                    </ol>
                  </div>
                  
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h5 className="font-medium text-blue-900 mb-2">Update Milestone - Request Format</h5>
                    <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">{`{
  "case_id": "case_xxxxx",
  "milestone": "complaint_filed",
  "action": "add"
}`}</pre>
                    <p className="text-xs text-blue-700 mt-2">
                      <strong>action:</strong> &quot;add&quot; to add milestone, &quot;remove&quot; to remove it
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      <strong>Valid milestones:</strong> letter_of_representation_sent, settlement_demand_sent, right_to_sue_obtained, complaint_filed, defendant_served, written_discovery_served, depositions_scheduled, mediation_scheduled, trial_date_assigned, settlement_reached, settlement_funds_disbursed, case_closed
                    </p>
                  </div>
                  
                  <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <h5 className="font-medium text-amber-900 mb-2">Update Case Status - Request Format</h5>
                    <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">{`{
  "case_id": "case_xxxxx",
  "status": "information_gathering",
  "status_description": "Optional description"
}`}</pre>
                    <p className="text-xs text-amber-700 mt-2">
                      <strong>Valid statuses:</strong> case_initiation, information_gathering, waiting_on_client, waiting_for_documents, pre_litigation_investigation, legal_analysis, preparing_settlement_demand, waiting_for_response, negotiations, drafting_complaint, attempting_service_of_process, waiting_for_defendant_response, preparing_default_judgment, waiting_for_arbitrator_assignment, drafting_written_discovery, waiting_for_discovery_responses, preparing_discovery_responses, meeting_and_conferring, drafting_motion, waiting_on_court_ruling, planning_depositions, planning_mediation, drafting_settlement_agreement, settlement_pending_signature, waiting_for_settlement_funds, lien_negotiations, waiting_for_check_clearance, drafting_final_accounting, final_accounting_pending, expert_discovery, preparing_for_trial, settlement_reached, case_closed
                    </p>
                  </div>
                  
                  <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                    <h5 className="font-medium text-emerald-900 mb-2">Update Case - Request Format</h5>
                    <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">{`{
  "case_id": "case_xxxxx",
  "case_name": "New Case Name",
  "case_type": "personal_injury",
  "description": "Updated description",
  "client_name": "John Doe",
  "client_email": "john@example.com",
  "client_phone": "555-1234",
  "is_archived": false
}`}</pre>
                    <p className="text-xs text-emerald-700 mt-2">
                      <strong>Note:</strong> All fields except case_id are optional. Only include fields you want to update.
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      <strong>Valid case_type:</strong> personal_injury, employment, other
                    </p>
                  </div>
                  
                  <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-100">
                    <h5 className="font-medium text-red-900 mb-2">Delete Case - Request Format</h5>
                    <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">{`{
  "case_id": "case_xxxxx",
  "permanent": false
}`}</pre>
                    <p className="text-xs text-red-700 mt-2">
                      <strong>permanent:</strong> Set to <code className="bg-red-100 px-1 rounded">false</code> to archive (soft delete) or <code className="bg-red-100 px-1 rounded">true</code> to permanently delete the case and all related data.
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      <strong>Warning:</strong> Permanent deletion removes the case, tasks, messages, documents, and appointments. This cannot be undone.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Create API Key Modal */}
      <Dialog open={showCreateKeyModal} onOpenChange={(open) => {
        setShowCreateKeyModal(open);
        if (!open) {
          setCreatedKey(null);
          setShowKeyValue(false);
        }
      }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              {createdKey ? 'API Key Created!' : 'Create API Key'}
            </DialogTitle>
          </DialogHeader>
          
          {createdKey ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium mb-2">
                  ⚠️ Copy this key now! It won't be shown again.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white rounded border font-mono text-sm break-all">
                    {showKeyValue ? createdKey.api_key : '•'.repeat(40)}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowKeyValue(!showKeyValue)}
                  >
                    {showKeyValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(createdKey.api_key)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p><strong>Name:</strong> {createdKey.name}</p>
                <p><strong>Prefix:</strong> {createdKey.key_prefix}</p>
              </div>
              <Button
                onClick={() => {
                  setShowCreateKeyModal(false);
                  setCreatedKey(null);
                }}
                className="w-full bg-[#80298F] hover:bg-[#6b2278]"
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                <Input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Zapier Production"
                  data-testid="api-key-name-input"
                />
                <p className="text-xs text-gray-500 mt-1">A friendly name to identify this key</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateKeyModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateApiKey}
                  className="flex-1 bg-[#80298F] hover:bg-[#6b2278]"
                  data-testid="confirm-create-key-btn"
                >
                  Create Key
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Case + Invite Client Modal */}
      <Dialog open={showCaseModal} onOpenChange={setShowCaseModal}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="case-modal">
          <DialogHeader>
            <DialogTitle>Create New Case + Invite Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCase} className="space-y-4">
            {/* Case Details Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b pb-2">Case Details</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Name *</label>
                <Input
                  value={newCase.case_name}
                  onChange={(e) => setNewCase({...newCase, case_name: e.target.value})}
                  placeholder="Smith v. Johnson"
                  required
                  data-testid="case-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case ID *</label>
                <Input
                  value={newCase.case_id_custom}
                  onChange={(e) => setNewCase({...newCase, case_id_custom: e.target.value})}
                  placeholder="e.g., 2026-PI-001"
                  required
                  data-testid="case-id-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Type *</label>
                <Select value={newCase.case_type} onValueChange={(val) => setNewCase({...newCase, case_type: val})}>
                  <SelectTrigger data-testid="case-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_injury">Personal Injury</SelectItem>
                    <SelectItem value="employment">Employment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Status *</label>
                <Select value={newCase.current_status} onValueChange={(val) => setNewCase({...newCase, current_status: val})}>
                  <SelectTrigger data-testid="case-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {caseStatusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Milestones <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto bg-gray-50">
                  {milestoneOptions.length === 0 ? (
                    <p className="text-sm text-gray-500">No milestones configured</p>
                  ) : (
                    <div className="space-y-2">
                      {milestoneOptions.map((milestone) => (
                        <label key={milestone.value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                          <input
                            type="checkbox"
                            checked={newCase.milestones.includes(milestone.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewCase({...newCase, milestones: [...newCase.milestones, milestone.value]});
                              } else {
                                setNewCase({...newCase, milestones: newCase.milestones.filter(m => m !== milestone.value)});
                              }
                            }}
                            className="h-4 w-4 text-[#80298F] border-gray-300 rounded focus:ring-[#80298F]"
                            data-testid={`milestone-checkbox-${milestone.value}`}
                          />
                          <span className="text-sm text-gray-700">{milestone.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {newCase.milestones.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{newCase.milestones.length} milestone(s) selected</p>
                )}
              </div>
            </div>

            {/* Client Details Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b pb-2">Client Details</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                <Input
                  value={newCase.client_name}
                  onChange={(e) => setNewCase({...newCase, client_name: e.target.value})}
                  placeholder="John Smith"
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Email *</label>
                <Input
                  type="email"
                  value={newCase.client_email}
                  onChange={(e) => setNewCase({...newCase, client_email: e.target.value})}
                  placeholder="client@example.com"
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Phone *</label>
                <Input
                  type="tel"
                  value={formatPhoneNumber(newCase.client_phone)}
                  onChange={(e) => setNewCase({...newCase, client_phone: formatPhoneInput(e.target.value)})}
                  placeholder="(310) 567-7777"
                  required
                  data-testid="client-phone-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#80298F] hover:bg-[#6B2278]" 
              data-testid="create-case-submit-button"
              disabled={creatingCase}
            >
              {creatingCase ? 'Creating...' : 'Create Case & Send Invitation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Staff Modal */}
      <Dialog open={showInviteStaffModal} onOpenChange={setShowInviteStaffModal}>
        <DialogContent className="bg-white" data-testid="invite-staff-modal">
          <DialogHeader>
            <DialogTitle>Invite New Staff Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInviteStaff} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff Name</label>
              <Input
                type="text"
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                placeholder="Jane Doe"
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff Email *</label>
              <Input
                type="email"
                value={staffEmail}
                onChange={(e) => setStaffEmail(e.target.value)}
                placeholder="staff@lawfirm.com"
                required
                data-testid="staff-email-input"
              />
            </div>
            <Button type="submit" className="w-full bg-[#80298F] hover:bg-[#6B2278]" data-testid="send-staff-invite-button">
              Send Invitation
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-white" data-testid="delete-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Deletion</DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
              {deleteTarget?.type === 'case' && (
                <span className="block mt-2 text-sm text-red-500">
                  This will also delete all messages, appointments, tasks, and documents associated with this case.
                </span>
              )}
              {deleteTarget?.type === 'client' && (
                <span className="block mt-2 text-sm text-orange-500">
                  Note: Cases associated with this client will remain but will no longer be linked to a client.
                </span>
              )}
              <span className="block mt-2 text-sm font-medium">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteTarget(null);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-button"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditUserModal} onOpenChange={(open) => {
        setShowEditUserModal(open);
        if (!open) {
          setEditUserTarget(null);
          setEditUserEmail('');
          setEditUserPassword('');
        }
      }}>
        <DialogContent className="bg-white" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle>Edit {editUserTarget?.type === 'staff' ? 'Staff Member' : 'Client'}</DialogTitle>
            <DialogDescription>
              Update email or reset password for <strong>{editUserTarget?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Update Email Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Update Email</h4>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={editUserEmail}
                  onChange={(e) => setEditUserEmail(e.target.value)}
                  placeholder="New email address"
                  className="flex-1"
                  data-testid="edit-user-email-input"
                />
                <Button
                  onClick={handleUpdateEmail}
                  className="bg-[#80298F] hover:bg-[#6B2278]"
                  data-testid="update-email-button"
                >
                  Update
                </Button>
              </div>
            </div>

            {/* Role Switcher (Staff/Admin only, not self) */}
            {editUserTarget?.type === 'staff' && editUserTarget?.user_id !== user.user_id && (
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-medium text-gray-900">Role</h4>
                <div className="flex gap-2 items-center">
                  <Select
                    value={editUserTarget?.role || 'staff'}
                    onValueChange={handleUpdateRole}
                  >
                    <SelectTrigger className="flex-1" data-testid="edit-user-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-gray-500">
                  Admins have full access including user management. Staff cannot manage other users.
                </p>
              </div>
            )}
            {editUserTarget?.type === 'staff' && editUserTarget?.user_id === user.user_id && (
              <div className="space-y-2 pt-4 border-t">
                <h4 className="font-medium text-gray-900">Role</h4>
                <p className="text-sm text-gray-500">You cannot change your own role.</p>
              </div>
            )}

            {/* Reset Password Section */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium text-gray-900">Reset Password</h4>
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={editUserPassword}
                  onChange={(e) => setEditUserPassword(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className="flex-1"
                  data-testid="edit-user-password-input"
                />
                <Button
                  onClick={handleResetPassword}
                  className="bg-[#FAA519] hover:bg-[#E59415] text-black"
                  data-testid="reset-password-button"
                >
                  Reset
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                This will log out the user from all devices.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;
